import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Stack,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Event as EventIcon,
  CalendarMonth as CalendarIcon,
  Videocam as VideoIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Fullscreen as FullscreenIcon,
  ArrowBack as ArrowBackIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from "@mui/icons-material";

import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../Redux/slices/authSlice";
import { CapitalizeName } from "../../../utils/capitalizeName";
import createInstance from "../../../api/axiosinstance";

// Components
import HRcandidate from "../HRcandidate";
import HRTable from "../HRcandidate/HRTable";
import JobDescription from "../../organization/JobDescription";
import JobDescriptionManagementList from "../../organization/JobDescriptionManagementList";
import ScheduleInterview from "../ScheduleInterview";
import InterviewsTable from "../InterviewsTable";
import ZoomMeeting from "../Zoom";
import logo from "../../../components/home/assets/images/logoAi.png";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";


const drawerWidth = 300;

// ==================== STYLED COMPONENTS (outside component to prevent remount) ====================
const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme: muiTheme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    width: drawerWidth,
    transition: muiTheme.transitions.create("width", {
      easing: muiTheme.transitions.easing.sharp,
      duration: muiTheme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    "& .MuiDrawer-paper": {
      width: drawerWidth,
      transition: muiTheme.transitions.create("width", {
        easing: muiTheme.transitions.easing.sharp,
        duration: muiTheme.transitions.duration.enteringScreen,
      }),
      overflowX: "hidden",
    },
  }),
  ...(!open && {
    transition: muiTheme.transitions.create("width", {
      easing: muiTheme.transitions.easing.sharp,
      duration: muiTheme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${muiTheme.spacing(7)} + 1px)`,
    [muiTheme.breakpoints.up("sm")]: {
      width: `calc(${muiTheme.spacing(8)} + 1px)`,
    },
    "& .MuiDrawer-paper": {
      transition: muiTheme.transitions.create("width", {
        easing: muiTheme.transitions.easing.sharp,
        duration: muiTheme.transitions.duration.leavingScreen,
      }),
      overflowX: "hidden",
      width: `calc(${muiTheme.spacing(7)} + 1px)`,
      [muiTheme.breakpoints.up("sm")]: {
        width: `calc(${muiTheme.spacing(8)} + 1px)`,
      },
    },
  }),
}));

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme: muiTheme, open }) => ({
  zIndex: muiTheme.zIndex.drawer + 1,
  transition: muiTheme.transitions.create(["width", "margin"], {
    easing: muiTheme.transitions.easing.sharp,
    duration: muiTheme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: muiTheme.transitions.create(["width", "margin"], {
      easing: muiTheme.transitions.easing.sharp,
      duration: muiTheme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme: muiTheme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: muiTheme.spacing(0, 1),
  ...muiTheme.mixins.toolbar,
}));

// ==================== MAIN COMPONENT ====================
const HRLandingPage = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const aiDisha = createInstance("DISHA");
  const organizationId = user?.organizationId;
  const userName = CapitalizeName(user?.name || "");
  const { COLORS, theme, toggleTheme } = useThemeMode();

  // State Management
  const [menudata, setMenudata] = useState("Dashboard");
  const [titleText, setTitleText] = useState("HR Dashboard");
  const [open, setOpen] = useState(true);
  const [showCreateNewJob, setShowCreateNewJob] = useState(false);
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Menu Configuration
  const menuItems = [
    { label: "Dashboard", icon: HomeIcon, key: "Dashboard" },
    { label: "Candidate Feedback", icon: PersonIcon, key: "Candidate FeedBack" },
    { label: "Manage Jobs", icon: WorkIcon, key: "Manage Jobs" },
    { label: "Schedule Interview", icon: EventIcon, key: "Schedule Interview" },
    { label: "Interviews", icon: CalendarIcon, key: "Interviews" },
    { label: "Zoom Records", icon: VideoIcon, key: "Zoom Records" },
  ];

  // Fetch Dashboard Data
  useEffect(() => {
    Dashboard();
  }, [organizationId]);

  const Dashboard = async () => {
    try {
      const response = await aiDisha.get(
        `/hr_dashboard?organization_id=${organizationId}`
      );
      console.log("dashboard", response.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  // Event Handlers
  const handleDrawerToggle = () => {
    setOpen(!open);
  };



    // Fetch Organization Details
    useEffect(() => {
      const fetchOrganization = async () => {
        try {
          setLoading(true);
          setError(null);
  
          console.log("Fetching organization data for user:", user);
  
          const orgId = user?.organizationId;
          if (!orgId) {
            setError("No organization ID found in user data");
            return;
          }
  
          const response = await aiDisha.get(`/get_organization_by_id?org_id=${orgId}`);
  
          console.log("API Response status:", response.status);
  
          if (response.status === 200 && response.data) {
            const data = response.data;
            console.log("Full API Response:", data);
  
            const orgData = data.organization || data;
            console.log("Organization data received from API:", orgData);
            console.log("Logo URL from API:", orgData.organization_logo_url);
            setOrganizationDetails(orgData);
          } else {
            console.warn("API call failed, checking user data...");
  
            const fallbackData = {
              organization_id: user?.organizationId,
              organization_name: user?.organizationName,
              organization_logo_url: user?.organizationLogoUrl,
              organization_domain: user?.organizationDomain,
            };
  
            if (fallbackData.organization_id) {
              console.log("Using fallback data:", fallbackData);
              setOrganizationDetails(fallbackData);
            } else {
              const errorText = await response.text();
              console.error("API error response:", errorText);
              setError(`Failed to fetch organization data: ${response.status}`);
            }
          }
        } catch (error) {
          console.error("Network error fetching organization data:", error);
          setError(`Network error: ${error.message}`);
        } finally {
          setLoading(false);
        }
      };
  
      fetchOrganization();
    }, [user?.organizationId]);
  
    // Fix Linode URL
    const fixLinodeUrl = (url) => {
      if (!url) return null;
  
      if (url.includes("ai-codedisha.s3.in-maa-1.linodeobjects.com")) {
        return url.replace(
          "ai-codedisha.s3.in-maa-1.linodeobjects.com",
          "in-maa-1.linodeobjects.com/ai-codedisha"
        );
      }
  
      return url;
    };







  const handleListItemClick = (item) => {
    if (item === "Schedule Interview") {
      handleOpenScheduleInterview();
    } else {
      setMenudata(item);
      setTitleText(item === "Dashboard" ? "HR Dashboard" : item);
      setShowCreateNewJob(false);
      setShowScheduleInterview(false);
    }
  };

  const handleOpenCreateNewJob = () => {
    setMenudata("");
    setTitleText("Create New Job");
    setShowCreateNewJob(true);
    setShowScheduleInterview(false);
  };

  const handleOpenScheduleInterview = () => {
    setMenudata("");
    setTitleText("Schedule Interview");
    setShowScheduleInterview(true);
    setShowCreateNewJob(false);
  };

  const handleCloseScheduleInterview = () => {
    setShowScheduleInterview(false);
    setMenudata("Dashboard");
    setTitleText("HR Dashboard");
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchor(null);
  };

  const handleLogOut = () => {
    handleProfileMenuClose();
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logout());
    setTimeout(() => navigate("/"), 0);
  };

  const handleFullscreen = () => {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  };



  if (!isAuthenticated || user?.role !== "HR") {
    navigate("/");
    return null;
  }

  return (
    <Box sx={{ display: "flex", background: COLORS.background, minHeight: "100vh" }}>
      {/* AppBar */}
      <StyledAppBar position="fixed" open={open} sx={{ background: COLORS.primary, borderBottom: `1px solid ${COLORS.border}` }}>
        <Toolbar sx={{ minHeight: "80px !important", display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ color: COLORS.accent }}
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: COLORS.text,
                letterSpacing: "0.5px",
              }}
            >
              {titleText}
            </Typography>
          </Box>

          {/* Right Side Actions */}
          <Stack direction="row" spacing={2} sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`} arrow>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: COLORS.text,
                  "&:hover": { background: COLORS.hoverBg },
                }}
              >
                {theme === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Fullscreen Mode (F11)" arrow>
              <IconButton
                onClick={handleFullscreen}
                sx={{
                  color: COLORS.text,
                  "&:hover": { background: COLORS.hoverBg },
                }}
              >
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
{/* Profile Button — Org Logo + Arrow */}
<Tooltip title="Account" placement="bottom">
  <Button
    onClick={handleProfileMenuOpen}
    sx={{
      display: { xs: "none", sm: "flex" },
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "2px",
      minWidth: "auto",
      padding: "4px 8px",
      borderRadius: "10px",
      color: COLORS.text,
      "&:hover": { background: COLORS.hoverBg },
    }}
  >
    {/* Org Logo or fallback */}
    {organizationDetails?.organization_logo_url ? (
      <Box
        component="img"
        src={fixLinodeUrl(organizationDetails.organization_logo_url)}
        alt="Org Logo"
        sx={{
          width: 100,
          height: 34,
          objectFit: "contain",
          borderRadius: "6px",
          background: "white",
          border: `1.5px solid ${Boolean(profileAnchor) ? COLORS.accent : COLORS.border}`,
          transition: "border-color 0.2s ease",
          padding: "2px",
        }}
      />
    ) : (
      <Avatar
        sx={{
          width: 34,
          height: 34,
          background: COLORS.accent,
          fontSize: "14px",
          fontWeight: 600,
          border: `1.5px solid ${Boolean(profileAnchor) ? COLORS.accent : "transparent"}`,
          transition: "border-color 0.2s ease",
        }}
      >
        {organizationDetails?.organization_name?.charAt(0)?.toUpperCase() || userName.charAt(0).toUpperCase()}
      </Avatar>
    )}

    {/* Small arrow below */}
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderTop: `4px solid ${COLORS.textSecondary}`,
        transition: "transform 0.2s ease",
        transform: Boolean(profileAnchor) ? "rotate(180deg)" : "rotate(0deg)",
        mt: "1px",
      }}
    />
  </Button>
</Tooltip>

{/* Dropdown Menu */}
<Menu
  anchorEl={profileAnchor}
  open={Boolean(profileAnchor)}
  onClose={handleProfileMenuClose}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  transformOrigin={{ vertical: "top", horizontal: "right" }}
  PaperProps={{
    elevation: 0,
    sx: {
      background: COLORS.primary,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "12px",
      mt: 1.5,
      minWidth: 260,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    },
  }}
>
  {/* Info Section — Org + User */}
  <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>

    {/* Org Row */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      {organizationDetails?.organization_logo_url ? (
        <Box
          component="img"
          src={fixLinodeUrl(organizationDetails.organization_logo_url)}
          alt="Org Logo"
          sx={{
            width: 38,
            height: 38,
            objectFit: "contain",
            borderRadius: "8px",
            background: "white",
            border: `1px solid ${COLORS.border}`,
            padding: "3px",
            flexShrink: 0,
          }}
        />
      ) : (
        <Avatar sx={{ width: 38, height: 38, background: COLORS.accent, borderRadius: "8px", fontSize: "16px", fontWeight: 600 }}>
          {organizationDetails?.organization_name?.charAt(0)?.toUpperCase() || "O"}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "11px", color: COLORS.textSecondary, lineHeight: 1.2 }}>
          Organization
        </Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {organizationDetails?.organization_name || "—"}
        </Typography>
      </Box>
    </Box>

    {/* Divider */}
    <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />

    {/* User Row */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Avatar sx={{ width: 38, height: 38, background: COLORS.accent, fontSize: "15px", fontWeight: 600, flexShrink: 0 }}>
        {userName.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {userName}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: COLORS.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {user?.email || "HR"}
        </Typography>
      </Box>
    </Box>

  </Box>

  <Divider sx={{ borderColor: COLORS.border }} />

  {/* Action Buttons */}
  <Box sx={{ py: 0.5 }}>

    <MenuItem
        disabled
      sx={{ fontSize: "13px", color: COLORS.textSecondary, padding: "10px 20px", gap: 1.5, opacity: 0.45 }}
    >
      <PersonOutlineIcon sx={{ fontSize: "17px" }} />
      Profile
      <Typography component="span" sx={{ ml: "auto", fontSize: "10px" }}>Soon</Typography>
    </MenuItem>

    <MenuItem
      disabled
      sx={{ fontSize: "13px", color: COLORS.textSecondary, padding: "10px 20px", gap: 1.5, opacity: 0.45 }}
    >
      <SettingsOutlinedIcon sx={{ fontSize: "17px" }} />
      Settings
      <Typography component="span" sx={{ ml: "auto", fontSize: "10px" }}>Soon</Typography>
    </MenuItem>

  </Box>

  <Divider sx={{ borderColor: COLORS.border }} />

  {/* Logout */}
  <MenuItem
    onClick={handleLogOut}
    sx={{
      fontSize: "13px",
      color: "#e53935",
      padding: "12px 20px",
      gap: 1.5,
      borderRadius: "0 0 12px 12px",
      "&:hover": { background: "rgba(229,57,53,0.06)" },
    }}
  >
    <LogoutIcon sx={{ fontSize: "17px" }} />
    Log out
  </MenuItem>
</Menu>
          </Stack>
        </Toolbar>
      </StyledAppBar>

      {/* Drawer */}
      <StyledDrawer variant="permanent" open={open}
        sx={{
          "& .MuiDrawer-paper": {
            background: COLORS.secondary,
            borderRight: `1px solid ${COLORS.border}`,
          },
        }}
      >
        <DrawerHeader
          sx={{
            minHeight: "70px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            padding: 0,
          }}
        >
          {open && isAuthenticated && (
            <>
              <Box
                sx={{
                  backgroundColor: COLORS.cardBg || "white",
                  padding: "15px",
                  borderRadius: "8px",
                  margin: "10px",
                  textAlign: "center",
                  gap: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                {organizationDetails?.organization_logo_url && (
                  <Box
                    component="img"
                    // src={fixLinodeUrl(organizationDetails?.organization_logo_url)}
                    src = {logo}
                    alt="Org Logo"
                    sx={{
                      background: "white",
                      maxWidth: "100%",     // 👈 important
                      height: "80px",
                      objectFit: "contain", // 👈 KEY CHANGE
                      padding:"8px",
                      borderRadius: "8px",
                    }}
                  />
                )}

                {/* {organizationDetails?.organization_name && (
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: COLORS.text,
                      maxWidth: "250px",
                      wordBreak: "break-word",
                      justifyContent: "center",
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                    }}
                  >
                    {organizationDetails?.organization_name}
                  </Typography>
                )}


               <Typography
                  sx={{
                    fontSize: "11px",
                    color: COLORS.textSecondary,
                    mt: 0.5,
                  }}
                >
                  HR Manager Dashboard
                </Typography> */}
              </Box>

              {/* User Welcome Section */}
              {/* <Box
                sx={{
                  backgroundColor: COLORS.primary,
                  padding: "15px",
                  borderRadius: "8px",
                  margin: "10px",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    background: COLORS.accent,
                    fontSize: "18px",
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {userName.charAt(0).toUpperCase()}
                </Avatar>

                <Tooltip title={`${user?.name || ""}`} placement="right">
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: "bold",
                      color: COLORS.text,
                      maxWidth: "160px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Welcome {userName}
                  </Typography>
                </Tooltip>

                <Typography
                  sx={{
                    fontSize: "11px",
                    color: COLORS.textSecondary,
                    mt: 0.5,
                  }}
                >
                  HR Manager
                </Typography>
              </Box> */}

              {/* #navigation */}
              <Box sx={{ pr: 2, width: "100%",  display: "flex", justifyContent: "center", }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: COLORS.accent,
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    mb: 1,
                  }}
                >
                  Navigation
                </Typography>
              </Box>
            </>
          )}
        </DrawerHeader>

        {/* Navigation List */}
        <List sx={{ color: COLORS.text, flex: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                onClick={() => handleListItemClick(item.key)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  "&:hover": {
                    background: COLORS.hoverBg,
                    color: COLORS.accent,
                  },
                  color: menudata === item.key ? COLORS.accent : COLORS.text,
                }}
              >
                <Tooltip title={item.label} placement="right" arrow>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  <item.icon sx={{ fontSize: "22px" }} />
                </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: open ? 1 : 0,
                    fontSize: "14px",
                    "& .MuiTypography-root": {
                      fontSize: "14px",
                      fontWeight: menudata === item.key ? 600 : 500,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Bottom Divider */}
        <Divider sx={{ borderColor: COLORS.border }} />

        {/* Bottom Actions */}
        {open && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              onClick={handleLogOut}
              sx={{
                color: COLORS.accent,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "14px",
                padding: "10px 12px",
                borderRadius: "6px",
                border: `1px solid ${COLORS.border}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: COLORS.hoverBg,
                  borderColor: COLORS.accent,
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: "18px" }} />
              Logout
            </Box>
          </Box>
        )}
      </StyledDrawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minHeight: "100vh",
          background: COLORS.background,
          overflow: "auto",
          marginTop: "80px", // Adjust for AppBar height
        }}
      >
        {/* Dashboard */}
        {menudata === "Dashboard" && <HRcandidate />}

        {/* Candidate Feedback */}
        {menudata === "Candidate FeedBack" && <HRTable />}

        {/* Manage Jobs */}
        {menudata === "Manage Jobs" && (
          <JobDescriptionManagementList
            onOpenJobDescription={handleOpenCreateNewJob}
          />
        )}

        {/* Create New Job */}
        {showCreateNewJob && <JobDescription />}

        {/* Schedule Interview */}
        {showScheduleInterview && (
          <ScheduleInterview onClose={handleCloseScheduleInterview} />
        )}

        {/* Interviews */}
        {menudata === "Interviews" && <InterviewsTable />}

        {/* Zoom Records */}
        {menudata === "Zoom Records" && <ZoomMeeting />}
      </Box>
    </Box>
  );
};

export default HRLandingPage;